import React, { useState, useRef, useEffect } from 'react';
import styles from '../../../../styles/PerformanceDashboard.module.css';

interface FloatingFilterProps {
  onApplyRange?: (startDate: string | null, endDate: string | null) => void;
  period?: string;
  onPeriodChange?: (period: string) => void;
}

type PresetKey =
  | 'today'
  | 'yesterday'
  | 'this_week'
  | 'last_7_days'
  | 'last_14_days'
  | 'this_month'
  | 'last_30_days'
  | 'last_month'
  | 'all_time';

const FloatingFilter: React.FC<FloatingFilterProps> = ({ onApplyRange, period, onPeriodChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [activePreset, setActivePreset] = useState<PresetKey>('this_month');
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const applyPreset = (preset: PresetKey) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let start = today;
    let end = today;

    switch (preset) {
      case 'today':
        start = today;
        end = today;
        break;
      case 'yesterday': {
        const y = new Date(today);
        y.setDate(y.getDate() - 1);
        start = y;
        end = y;
        break;
      }
      case 'this_week': {
        const day = today.getDay(); // 0 (Sun) - 6 (Sat)
        const diff = day === 0 ? 6 : day - 1; // make Monday start
        const weekStart = new Date(today);
        weekStart.setDate(weekStart.getDate() - diff);
        start = weekStart;
        end = today;
        break;
      }
      case 'last_7_days': {
        const s = new Date(today);
        s.setDate(s.getDate() - 6);
        start = s;
        end = today;
        break;
      }
      case 'last_14_days': {
        const s = new Date(today);
        s.setDate(s.getDate() - 13);
        start = s;
        end = today;
        break;
      }
      case 'this_month': {
        const s = new Date(today.getFullYear(), today.getMonth(), 1);
        start = s;
        end = today;
        break;
      }
      case 'last_30_days': {
        const s = new Date(today);
        s.setDate(s.getDate() - 29);
        start = s;
        end = today;
        break;
      }
      case 'last_month': {
        const s = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const e = new Date(today.getFullYear(), today.getMonth(), 0);
        start = s;
        end = e;
        break;
      }
      case 'all_time':
      default:
        start = new Date(today.getFullYear() - 5, 0, 1);
        end = today;
        break;
    }

    setActivePreset(preset);
    setStartDate(formatDate(start));
    setEndDate(formatDate(end));
  };

  const handleApply = () => {
    if (onApplyRange) {
      onApplyRange(startDate || null, endDate || null);
    }
    setIsOpen(false);
  };

  const handleClear = () => {
    setStartDate('');
    setEndDate('');
    setActivePreset('this_month');
    if (onApplyRange) {
      onApplyRange(null, null);
    }
    setIsOpen(false);
  };

  return (
    <div className={styles.floatingFilter} ref={panelRef}>
      <button
        type="button"
        className={styles.floatingFilterButton}
        onClick={() => setIsOpen(!isOpen)}
        data-tooltip="Filter by date range"
      >
        <span className={styles.floatingNavIcon}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16"></path>
            <path d="M10 12h10"></path>
            <path d="M6 20h8"></path>
          </svg>
        </span>
      </button>

      {isOpen && (
        <div className={styles.floatingFilterPanel}>
          <div className={styles.floatingFilterHeader}>
            <h4>Time Period</h4>
            <p>Select a time period to filter performance data.</p>
          </div>
          <div className={styles.floatingFilterContent}>
            {onPeriodChange && (
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                  Period
                </label>
                <select
                  value={period || 'all'}
                  onChange={(e) => {
                    onPeriodChange(e.target.value);
                    setIsOpen(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: '#ffffff',
                    color: '#374151',
                    cursor: 'pointer'
                  }}
                >
                  <option value="all">All Time</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                  <option value="quarter">Last 90 Days</option>
                  <option value="year">Last 365 Days</option>
                </select>
              </div>
            )}
            <div className={styles.filterPresets}>
              <button
                type="button"
                className={`${styles.filterPresetItem} ${activePreset === 'today' ? styles.activePreset : ''}`}
                onClick={() => applyPreset('today')}
              >
                Today
              </button>
              <button
                type="button"
                className={`${styles.filterPresetItem} ${activePreset === 'yesterday' ? styles.activePreset : ''}`}
                onClick={() => applyPreset('yesterday')}
              >
                Yesterday
              </button>
              <button
                type="button"
                className={`${styles.filterPresetItem} ${activePreset === 'this_week' ? styles.activePreset : ''}`}
                onClick={() => applyPreset('this_week')}
              >
                This week (Mon – Today)
              </button>
              <button
                type="button"
                className={`${styles.filterPresetItem} ${activePreset === 'last_7_days' ? styles.activePreset : ''}`}
                onClick={() => applyPreset('last_7_days')}
              >
                Last 7 days
              </button>
              <button
                type="button"
                className={`${styles.filterPresetItem} ${activePreset === 'last_14_days' ? styles.activePreset : ''}`}
                onClick={() => applyPreset('last_14_days')}
              >
                Last 14 days
              </button>
              <button
                type="button"
                className={`${styles.filterPresetItem} ${activePreset === 'this_month' ? styles.activePreset : ''}`}
                onClick={() => applyPreset('this_month')}
              >
                This month
              </button>
              <button
                type="button"
                className={`${styles.filterPresetItem} ${activePreset === 'last_30_days' ? styles.activePreset : ''}`}
                onClick={() => applyPreset('last_30_days')}
              >
                Last 30 days
              </button>
              <button
                type="button"
                className={`${styles.filterPresetItem} ${activePreset === 'last_month' ? styles.activePreset : ''}`}
                onClick={() => applyPreset('last_month')}
              >
                Last month
              </button>
              <button
                type="button"
                className={`${styles.filterPresetItem} ${activePreset === 'all_time' ? styles.activePreset : ''}`}
                onClick={() => applyPreset('all_time')}
              >
                All time
              </button>
            </div>
            <div className={styles.floatingFilterBody}>
              <div className={styles.dateField}>
                <label>Start date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className={styles.dateField}>
                <label>End date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className={styles.floatingFilterFooter}>
            <button type="button" onClick={handleClear} className={styles.filterClearButton}>
              Clear
            </button>
            <button type="button" onClick={handleApply} className={styles.filterApplyButton}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FloatingFilter;


